// src/components/NotificationsDropdown.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "../axios";
import "./NotificationsDropdown.css";
import { useNavigate } from "react-router-dom";
import { getNotificationMeta } from "./notificationTypes";

export default function NotificationsDropdown({ onClose, onOpenAll }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [isStandaloneApp, setIsStandaloneApp] = useState(false);

  useEffect(() => {
    const load = async () => {
      setErr("");

      if (!token) {
        setLoading(false);
        setItems([]);
        return;
      }

      try {
        setLoading(true);
        // ✅ последние 15 (можешь поменять)
        const res = await axios.get("/api/notifications/", { params: { limit: 15 } });
        setItems(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error("Σφάλμα φόρτωσης ειδοποιήσεων:", e);
        setErr("❌ Σφάλμα φόρτωσης ειδοποιήσεων.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token]);

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;

    setIsStandaloneApp(standalone);
  }, []);

  const hasUnread = useMemo(() => items.some((n) => !n.is_read), [items]);

  const formatTime = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return "";
    }
  };

  const shortTitle = (title, max = 10) => {
    if (!title) return "";
    if (title.length <= max) return title;
    return title.slice(0, max) + "…";
  };

  const getNotificationTitle = (n, meta) => {
    const count = Array.isArray(n.target_ad_ids)
      ? n.target_ad_ids.length
      : Array.isArray(n.ad_titles)
        ? n.ad_titles.length
        : 0;

    if (n.notif_type === "favorite_ad_price_changed") {
      return count > 1
        ? `Μειώθηκε η τιμή σε ${count} αποθηκευμένες αγγελίες:`
        : "Μειώθηκε η τιμή σε αποθηκευμένη αγγελία:";
    }

    if (n.notif_type === "favorite_ad_completed") {
      return count > 1
        ? `${count} αποθηκευμένες αγγελίες ολοκληρώθηκαν:`
        : meta.title;
    }

    if (n.notif_type === "new_ad") {
      if (count > 1) {
        return `${n.actor?.name || "DEAL"} δημοσίευσε ${count} νέες αγγελίες:`;
      }

      return n.text || `${n.actor?.name || "DEAL"} ${meta.title}`;
    }

    return n.text || `${n.actor?.name || "DEAL"} ${meta.title}`;
  };

  const markRead = async (id) => {
    try {
      // ✅ если у тебя другой endpoint — скажи, я подстрою
      await axios.post(`/api/notifications/${id}/read/`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    } catch (e) {
      console.warn("Не удалось отметить прочитанным:", e);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post("/api/notifications/read-all/");
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      window.dispatchEvent(new Event("refresh-unread-count"));
    } catch (e) {
      console.warn("Δεν είναι δυνατή η επισήμανση όλων ως αναγνωσμένων:", e);
    }
  };

  const handleItemClick = async (n) => {
    await markRead(n.id);
    onClose && onClose();

    const open = (url) => {
      if (isStandaloneApp) {
        navigate(url);
        return;
      }
      window.open(url, "_blank");
    };

    // ===== 📦 ОБЪЯВЛЕНИЕ ЗАВЕРШЕНО (ИЗ ИЗБРАННОГО) =====
    if (n.notif_type === "favorite_ad_completed" && n.target_profile_id) {
      const ids = Array.isArray(n.target_ad_ids) ? n.target_ad_ids : [];
      const qs = ids.length
        ? `?tab=closed&highlightIds=${encodeURIComponent(ids.join(","))}`
        : `?tab=closed`;

      open(`/seller/${n.target_profile_id}${qs}`);
      return;
    }

    // ===== 📉 СНИЖЕНИЕ ЦЕНЫ В ИЗБРАННОМ =====
    if (n.notif_type === "favorite_ad_price_changed" && n.target_profile_id) {
      const ids = Array.isArray(n.target_ad_ids) ? n.target_ad_ids : [];
      const qs = ids.length
        ? `?highlight=priceChanged&adIds=${encodeURIComponent(ids.join(","))}`
        : `?highlight=priceChanged`;

      open(`/seller/${n.target_profile_id}${qs}`);
      return;
    }


    // ===== 🆕 НОВЫЕ ОБЪЯВЛЕНИЯ ПОДПИСОК =====
    if (n.notif_type === "new_ad" && n.target_profile_id) {
      const ids = Array.isArray(n.target_ad_ids) ? n.target_ad_ids : [];
      const qs = ids.length
        ? `?highlight=newAds&adIds=${encodeURIComponent(ids.join(","))}`
        : `?highlight=newAds`;

      open(`/seller/${n.target_profile_id}${qs}`);
      return;
    }

    // ===== 💬 NEW MESSAGE =====
    if (n.notif_type === "new_message" && n.target_conversation_id) {
      navigate(`/chat?conversation=${n.target_conversation_id}`);
      return;
    }

    // ===== 📄 ОТКРЫТЬ ОБЪЯВЛЕНИЕ =====
    if (n.target_ad_id) {
      open(`/ads/${n.target_ad_id}`);
      return;
    }

    // ===== 👤 ОТКРЫТЬ ПРОФИЛЬ =====
    if (n.target_profile_id) {
      open(`/seller/${n.target_profile_id}`);
      return;
    }
  };

  return (
    <div className="notif-dd">
      <div className="notif-dd__header">
        <div className="notif-dd__title">Ειδοποιήσεις</div>

        <button
          type="button"
          className="notif-dd__btn"
          onClick={markAllRead}
          disabled={!hasUnread}
          title="Σήμανση όλων ως αναγνωσμένων"
        >
          ✓ Αναγνωσμένα
        </button>
      </div>

      <div className="notif-dd__body">
        {loading ? (
          <div className="notif-dd__empty">Φόρτωση...</div>
        ) : err ? (
          <div className="notif-dd__empty">{err}</div>
        ) : items.length === 0 ? (
          <div className="notif-dd__empty">Δεν υπάρχουν ειδοποιήσεις.</div>
        ) : (
          items.map((n) => {
            const meta = getNotificationMeta(n.notif_type);

            return (
              <button
                key={n.id}
                type="button"
                className={`notif-dd__item ${n.is_read ? "is-read" : "is-unread"}`}
                onClick={() => handleItemClick(n)}
              >
                <div className="notif-dd__row">

                  {/* 👤 АВАТАР */}
                  <div className="notif-dd__avatar">
                    {n.actor?.avatar ? (
                      <img src={n.actor.avatar} alt="" />
                    ) : (
                      <div className="notif-dd__avatarFallback">
                        {(n.actor?.name || "D").charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  {/* 🔔 ИКОНКА ТИПА УВЕДОМЛЕНИЯ */}
                  <div
                    className="notif-dd__icon"
                    style={{ background: meta.color + "20", color: meta.color }}
                  >
                    {meta.icon}
                  </div>

                  {/* 📝 ТЕКСТ УВЕДОМЛЕНИЯ */}
                  <div className="notif-dd__content">
                    <div className="notif-dd__itemTitle">
                      <span className="notif-dd__titleText">
                        {getNotificationTitle(n, meta)}
                      </span>
                    </div>

                    <div className="notif-dd__itemBody">
                      {n.ad_titles?.length
                        ? (() => {
                            const titles = n.ad_titles.map(t => shortTitle(t, 10));
                            const first3 = titles.slice(0, 3);
                            const rest = titles.length - 3;

                            return rest > 0
                              ? `${first3.join(" • ")} +${rest}`
                              : first3.join(" • ");
                          })()
                        : (
                            n.notif_type === "favorite_ad_completed"
                              ? ""
                              : n.notif_type === "new_message"
                                ? (n.message_preview || "Νέο μήνυμα")
                                : (n.text || "Ειδοποίηση")
                          )}
                    </div>

                    <div className="notif-dd__itemMeta">
                      {formatTime(n.created_at)}
                    </div>
                  </div>

                </div>
              </button>
            );
          })
        )}
      </div>

      <div className="notif-dd__footer">
        <button
          type="button"
          className="notif-dd__link"
          onClick={() => {
            onClose && onClose();
            onOpenAll && onOpenAll();
          }}
        >
          Προβολή όλων →
        </button>
      </div>
    </div>
  );
}
